import { useEffect, useCallback } from 'react';
import { notification } from 'antd';
import notificationService from '../services/NotificationService';
import { useNotificationContext } from '../contexts/NotificationContext';
import Config from '../utils/Config';

const useNotifications = (userId) => {
    const { setCompletionStatus } = useNotificationContext();

    // Connect to SSE when component mounts (only if SSE is enabled)
    useEffect(() => {
        // Early return if SSE is disabled - don't attempt any connection
        // Double-check the config value to ensure it's being read correctly
        if (!Config.ENABLE_SSE) {
            // Also ensure any existing connection is disconnected
            notificationService.disconnect();
            return;
        }
        
        if (userId) {
            notificationService.connect(userId);
        } else {
            console.warn('No userId provided to useNotifications hook');
        }

        // Don't disconnect on unmount - let the connection persist
        // The connection will be managed globally by the NotificationService
        return () => {
            // Only log if SSE is enabled
            if (Config.ENABLE_SSE) {
                console.log('useNotifications: Component unmounting, but keeping SSE connection alive');
            }
        };
    }, [userId]);

    // Handle cancellation complete notifications
    const handleCancellationComplete = useCallback((data) => {
        const { successCount, failedCount, totalCount } = data.data;
        
        if (successCount === totalCount) {
            // All successful
            const message = `Cancellation Completed - All ${totalCount} documents cancelled successfully`;
            setCompletionStatus(message, 'completed');
        } else if (successCount > 0 && failedCount > 0) {
            // Partial success
            const message = `Cancellation Completed - ${successCount} successful, ${failedCount} failed out of ${totalCount} documents`;
            setCompletionStatus(message, 'completed');
        } else {
            // All failed
            const message = `Cancellation Completed - All ${totalCount} documents failed to cancel`;
            setCompletionStatus(message, 'completed');
        }
    }, [setCompletionStatus]);

    // Handle connection status changes
    const handleConnectionStatus = useCallback((data) => {
        if (data.status === 'connected') {
            console.log('Real-time notifications connected');
        } else if (data.status === 'disconnected') {
            console.log('Real-time notifications disconnected');
        } else if (data.status === 'error') {
            console.error('Real-time notifications error:', data.error);
        }
    }, []);

    // Set up event listeners
    useEffect(() => {
        // Listen for cancellation complete events
        notificationService.addEventListener('cancellation_complete', handleCancellationComplete);
        
        // Listen for connection status changes
        notificationService.addEventListener('connection', handleConnectionStatus);

        // Cleanup listeners
        return () => {
            notificationService.removeEventListener('cancellation_complete', handleCancellationComplete);
            notificationService.removeEventListener('connection', handleConnectionStatus);
        };
    }, [handleCancellationComplete, handleConnectionStatus]);

    // Return connection status and utility functions
    return {
        isConnected: notificationService.getConnectionStatus(),
        connect: (userId) => notificationService.connect(userId),
        disconnect: () => notificationService.disconnect(),
        addEventListener: (eventType, callback) => notificationService.addEventListener(eventType, callback),
        removeEventListener: (eventType, callback) => notificationService.removeEventListener(eventType, callback),
    };
};

export default useNotifications;
